import { useQuery } from "@tanstack/react-query";
import { Post } from "../../../utils/apiService";
import {
  GetAllSearchApprovePermitRequestsBody,
  GetIssuanceRenewalIdForStaffFromContractedCompaniesByIdBody,
  GetLostVehicleStickerIDCardByIdBody,
  GetOfficeAccessAuthorizationByIdBody,
  GetParkingStickerByIdBody,
  GetPCPermitByIdBody,
  GetPermitToWorkByIdBody,
  GetWarehouseInOutPermitByIdBody,
} from "../interface";
import { APP_ROUTES } from "../../../routes";

export const FETCH_PERMIT_DETAILS_KEYS = {
  FETCH_ALL_SEARCH_PERMIT_REQUESTS: "FETCH_ALL_SEARCH_PERMIT_REQUESTS",
  FETCH_PC_PERMIT_BY_ID: "FETCH_PC_PERMIT_BY_ID",
  FETCH_WAREHOUSE_IN_OUT_PERMIT_BY_ID: "FETCH_WAREHOUSE_IN_OUT_PERMIT_BY_ID",
  FETCH_SAFETY_CHECKLIST: "FETCH_SAFETY_CHECKLIST",
  FETCH_PERMIT_TO_WORK_BY_ID: "FETCH_PERMIT_TO_WORK_BY_ID",
  FETCH_LOST_VEHICLE_STICKER_ID_CARD_BY_ID: "FETCH_LOST_VEHICLE_STICKER_ID_CARD_BY_ID",
  FETCH_PARKING_STICKER_BY_ID: "FETCH_PARKING_STICKER_BY_ID",
  FETCH_VEHICLE_ACCESS_DROPDOWN: "FETCH_VEHICLE_ACCESS_DROPDOWN",
  FETCH_OFFICE_ACCESS_AUTHORIZATION_BY_ID: "FETCH_OFFICE_ACCESS_AUTHORIZATION_BY_ID",
  FETCH_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES_BY_ID: "FETCH_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES_BY_ID"
};

const getVehicleAccessDropdownAction = async () => {
  return Post({
    url: "/api/ParkingEntrySticker/GetVehicleAccessDropdown",
  });
};

export const useGetVehicleAccessDropdown = () => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_VEHICLE_ACCESS_DROPDOWN,
    ],
    queryFn: () => getVehicleAccessDropdownAction(),
  });
};

const getPermitToWorkByIdAction = async (body: GetPermitToWorkByIdBody) => {
  return Post({
    url: "/api/PermitToWork/GetById",
    body,
  });
};

export const useGetPermitToWorkById = (body: GetPermitToWorkByIdBody) => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_PERMIT_TO_WORK_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getPermitToWorkByIdAction(body),
    enabled: !!body.encryptedId,
  });
};

const getAllSearchApprovePermitRequestsAction = async (
  body: GetAllSearchApprovePermitRequestsBody,
  locationPath: string,
) => {
  return Post({
    url:
      locationPath === APP_ROUTES.SEARCH_PERMIT_REQUEST
        ? "/api/Permits/GetAll"
        : "/api/Permits/Approvals",
    body,
  });
};

export const useGetAllSearchApprovePermitRequests = (
  body: GetAllSearchApprovePermitRequestsBody,
  locationPath: string,
) => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_ALL_SEARCH_PERMIT_REQUESTS,
      body,
    ],
    queryFn: () => getAllSearchApprovePermitRequestsAction(body, locationPath),
  });
};

const getPCPermitByIdAction = async (body: GetPCPermitByIdBody) => {
  return Post({
    url: "/api/PermitLaptop/GetById",
    body,
  });
};

export const useGetPCPermitById = (body: GetPCPermitByIdBody) => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_PC_PERMIT_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getPCPermitByIdAction(body),
    enabled: !!body.encryptedId,
  });
};

const getLostVehicleStickerIDCardByIdAction = async (body: GetLostVehicleStickerIDCardByIdBody) => {
  return Post({
    url: "/api/LostStickerRequest/GetById",
    body,
  });
};

export const useGetLostVehicleStickerIDCardById = (body: GetLostVehicleStickerIDCardByIdBody) => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_LOST_VEHICLE_STICKER_ID_CARD_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getLostVehicleStickerIDCardByIdAction(body),
    enabled: !!body.encryptedId,
  });
};

const getWarehouseInOutPermitByIdAction = async (
  body: GetWarehouseInOutPermitByIdBody,
) => {
  return Post({
    url: "/api/PermitWarehouse/GetById",
    body,
  });
};

export const useGetWarehouseInOutPermitById = (
  body: GetWarehouseInOutPermitByIdBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_WAREHOUSE_IN_OUT_PERMIT_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getWarehouseInOutPermitByIdAction(body),
    enabled: !!body.encryptedId,
  });
};

const getSafetyChecklistAction = async () => {
  return Post({
    url: "/api/PermitToWork/SafetyChecklist/Master",
  });
};

export const useGetSafetyChecklist = () => {
  return useQuery({
    queryKey: [FETCH_PERMIT_DETAILS_KEYS.FETCH_SAFETY_CHECKLIST],
    queryFn: () => getSafetyChecklistAction(),
  });
};

const getParkingStickerByIdAction = async (
  body: GetParkingStickerByIdBody,
) => {
  return Post({
    url: "/api/ParkingEntrySticker/GetById",
    body,
  });
};

export const useGetParkingStickerById = (
  body: GetParkingStickerByIdBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_PARKING_STICKER_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getParkingStickerByIdAction(body),
    enabled: !!body.encryptedId,
  });
};

const getOfficeAccessAuthorizationByIdAction = async (
  body: GetOfficeAccessAuthorizationByIdBody,
) => {
  return Post({
    url: "/api/OfficeAccessAuthorisation/GetById",
    body,
  });
};

export const useGetOfficeAccessAuthorizationById = (
  body: GetOfficeAccessAuthorizationByIdBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_OFFICE_ACCESS_AUTHORIZATION_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getOfficeAccessAuthorizationByIdAction(body),
    enabled: !!body.encryptedId,
  });
};

// 
const getIssuanceRenewalIdForStaffFromContractedCompaniesByIdAction = async (
  body: GetIssuanceRenewalIdForStaffFromContractedCompaniesByIdBody,
) => {
  return Post({
    url: "/api/ContractorStaffId/GetById",
    body,
  });
};

export const useGetIssuanceRenewalIdForStaffFromContractedCompaniesById = (
  body: GetIssuanceRenewalIdForStaffFromContractedCompaniesByIdBody,
) => {
  return useQuery({
    queryKey: [
      FETCH_PERMIT_DETAILS_KEYS.FETCH_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES_BY_ID,
      body.encryptedId,
    ],
    queryFn: () => getIssuanceRenewalIdForStaffFromContractedCompaniesByIdAction(body),
    enabled: !!body.encryptedId,
  });
};